import { Component, type ErrorInfo, type ReactNode } from 'react'
import styles from './ErrorBoundary.module.css'

/**
 * The last line before a blank page.
 *
 * `src/lib/supabase.ts` throws when its env vars are missing, which is the
 * right call in development and a white screen in a production build. A couple
 * who hits that cannot tell a broken deploy from a broken laptop, so say which.
 */
export function FatalError({ error }: { error: Error }) {
  /* Any missing build-time variable, not only the Supabase pair. A regex
     naming one variable means the next guard someone adds falls through to
     "Something broke on our side", which blames the app for what is a setup
     problem — the exact thing this component exists to avoid.

     Only supabase.ts throws at module load today, and only it should: auth
     gates every screen, so there is nothing to degrade to. A guard for
     something narrower belongs at its point of use (see `post` in
     mailbox.ts), where it never reaches this boundary at all. */
  const config = /VITE_/.test(error.message)

  return (
    <div className={styles.shell}>
      <div className={styles.panel}>
        <p className={styles.eyebrow}>Ever After</p>
        <h1>{config ? 'This copy is not set up yet' : 'Something broke on our side'}</h1>
        <p className={styles.lede}>
          {config
            ? 'This copy is missing part of its configuration, so it cannot reach your account. Nothing you did caused this.'
            : 'Reloading usually clears it. If it keeps happening, tell us and we will look.'}
        </p>
        {/* The raw message is for whoever is debugging, not for her. */}
        <p className={styles.detail}>{error.message}</p>
        <button type="button" className={styles.btn} onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  )
}

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/** Catches render-time and lifecycle errors. Import-time throws are caught by
    the dynamic import in `main.tsx` — a boundary never mounts for those. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[boundary]', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    return error ? <FatalError error={error} /> : this.props.children
  }
}
